import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import Stadtplan_Bild from '../assets/Stadtplan.jpg';
import Stadtplan_Marker from '../assets/kreis.svg';
import './Stadtplan.css';
import { host_url } from '../App'; // Import host_url
import { useLocation } from 'react-router-dom';
import KernteamLayout from '../KernteamLayout';

function Stadtplan() {
  const [jobs, setJobs] = useState([]);
  const [offers, setOffers] = useState([]);
  const [selectedItemId, setSelectedItemId] = useState(null); // Only store ID
  const stadtplanRef = useRef(null);
  const containerRef = useRef(null); // Ref for the container div
  const location = useLocation();

  const [zoomLevel, setZoomLevel] = useState(1); // Default zoom level
  const [pan, setPan] = useState({ x: 0, y: 0 }); // Default pan position
  const [naturalImageDimensions, setNaturalImageDimensions] = useState(null);
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [isPinching, setIsPinching] = useState(false);
   const pinchStateRef = useRef({
    initialDistance: 0,
    initialZoom: 1,
  });


  // Fetch jobs
  useEffect(() => {
    fetch(`${host_url}/jobs`)
      .then(response => response.json())
      .then(data => {
        const sortedData = data.sort((a, b) => a.Name.localeCompare(b.Name));
        setJobs(sortedData);
      })
      .catch(error => console.error('Error fetching jobs:', error));
  }, []); // Fetch once

  // Fetch offers
  useEffect(() => {
    fetch(`${host_url}/freizeitangebote`)
      .then(response => response.json())
      .then(data => {
        const sortedData = data.sort((a, b) => a.Name.localeCompare(b.Name));
        setOffers(sortedData);
      })
      .catch(error => console.error('Error fetching offers:', error));
  }, []); // Fetch once

  // Effect for initial item selection from URL, once data is loaded
  useEffect(() => {
    if (jobs.length === 0 && offers.length === 0 && !location.search) return; // Wait for data or if no search params

    const params = new URLSearchParams(location.search);
    const jobIdParam = params.get('jobId');
    const offerIdParam = params.get('offerId');

    let itemToSelect = null;

    if (offerIdParam && offers.length > 0) {
      itemToSelect = { type: 'offer', id: parseInt(offerIdParam) }; // Store as object
    }
    
    if (!itemToSelect && jobIdParam && jobs.length > 0) {
      itemToSelect = { type: 'job', id: parseInt(jobIdParam) }; // Correctly store as {type, id} object
    }

    if (itemToSelect) {
      setSelectedItemId(itemToSelect);
      // Initial view adjustment is now handled in the useEffect below
    }
  }, [location.search, jobs, offers]);

  // Get natural image dimensions
  useEffect(() => {
    const image = stadtplanRef.current;
    if (image) {
      const handleLoad = () => {
        setNaturalImageDimensions({
          width: image.naturalWidth,
          height: image.naturalHeight,
        });
      };
      if (image.complete) {
        handleLoad();
      } else {
        image.addEventListener('load', handleLoad);
      }
      return () => {
        image.removeEventListener('load', handleLoad);
      };
    }
  }, []);

  // Memoize selectedItem to prevent unnecessary recalculations
  const selectedItem = useMemo(() => {
    if (!selectedItemId) return null;
    if (selectedItemId.type === 'job') {
      return jobs.find(job => job.JobID === selectedItemId.id);
    } else if (selectedItemId.type === 'offer') {
      return offers.find(offer => offer.ID === selectedItemId.id);
    }
    return null;
  }, [selectedItemId, jobs, offers]);

  // Adjust initial view or when selectedItem changes
  useEffect(() => {
    if (!naturalImageDimensions || !containerRef.current) return;

    const containerWidth = containerRef.current.offsetWidth;
    const containerHeight = containerRef.current.offsetHeight;
    const imageWidth = naturalImageDimensions.width;
    const imageHeight = naturalImageDimensions.height;
    
    const PREFERRED_ZOOM_FOR_SELECTED_ITEM = 1.1; // Bevorzugter Zoom-Level für ausgewählte Items
    const MAX_OVERALL_ZOOM = 5; // Maximaler Zoom-Level (konsistent mit manuellem Zoom)
    const MIN_OVERALL_ZOOM = 0.25; // Minimaler Zoom-Level (konsistent mit manuellem Zoom)
    const MAX_ZOOM_CAP_WHEN_FITTING_IMAGE = 1; // Beim Anpassen an Bildschirmgröße: nicht weiter als 1 herauszoomen, falls Bild kleiner als Container

    let newZoomLevel;
    let newPanX = 0;
    let newPanY = 0;

    if (selectedItem && typeof selectedItem.X === 'number' && typeof selectedItem.Y === 'number') {
      // Ein Item ist ausgewählt: Zoom auf PREFERRED_ZOOM_FOR_SELECTED_ITEM
      newZoomLevel = Math.min(PREFERRED_ZOOM_FOR_SELECTED_ITEM, MAX_OVERALL_ZOOM);
      newZoomLevel = Math.max(newZoomLevel, MIN_OVERALL_ZOOM); // Sicherstellen, dass der Zoom nicht unter das Minimum fällt

      // Pan-Position berechnen, um das Item zu zentrieren
      const itemX = (selectedItem.X / 1000) * imageWidth * newZoomLevel;
      const itemY = (selectedItem.Y / 1000) * imageHeight * newZoomLevel;

      newPanX = containerWidth / 2 - itemX;
      newPanY = containerHeight / 2 - itemY;
    } else {
      // Kein Item ausgewählt oder Item hat keine Koordinaten: Bild an Containergröße anpassen
      const zoomToFitWidth = containerWidth / imageWidth;
      const zoomToFitHeight = containerHeight / imageHeight;
      let calculatedFitZoom = Math.min(zoomToFitWidth, zoomToFitHeight);
      
      newZoomLevel = Math.min(calculatedFitZoom, MAX_ZOOM_CAP_WHEN_FITTING_IMAGE);
      newZoomLevel = Math.max(newZoomLevel, MIN_OVERALL_ZOOM); // Sicherstellen, dass der Zoom nicht unter das Minimum fällt

      // Bild zentrieren
      newPanX = (containerWidth - imageWidth * newZoomLevel) / 2;
      newPanY = (containerHeight - imageHeight * newZoomLevel) / 2;
    }

    setZoomLevel(newZoomLevel);
    setPan({ x: newPanX, y: newPanY });

  }, [naturalImageDimensions, containerRef, selectedItem]);

  const handleItemChange = (event) => {
    const [type, idStr] = event.target.value.split('-');
    const id = parseInt(idStr);
    if (type === 'job') {
      setSelectedItemId({ type: 'job', id });
    } else if (type === 'offer') {
      setSelectedItemId({ type: 'offer', id });
    }
  };

  const getMarkerPosition = () => {
    if (!selectedItem || !naturalImageDimensions || typeof selectedItem.X !== 'number' || typeof selectedItem.Y !== 'number') {
      return { left: '-1000px', top: '-1000px' }; // Hide marker if conditions are not met
    }

    const imageWidth = naturalImageDimensions.width;
    const imageHeight = naturalImageDimensions.height;
    
    // Calculate item position with the current zoom level
    const itemX = (selectedItem.X / 1000) * imageWidth;
    const itemY = (selectedItem.Y / 1000) * imageHeight;

    const left = itemX * zoomLevel + pan.x;
    const top = itemY * zoomLevel + pan.y;

    return {
      left: `${left}px`,
      top: `${top}px`,
    };
  };

  const adjustPanForZoom = (prevZoom, nextZoom, zoomCenterX, zoomCenterY) => {
    const newPanX = zoomCenterX - ((zoomCenterX - pan.x) / prevZoom) * nextZoom;
    const newPanY = zoomCenterY - ((zoomCenterY - pan.y) / prevZoom) * nextZoom;
    return { x: newPanX, y: newPanY };
  };

  const handleManualZoom = (zoomIncrement) => {
    if (!containerRef.current || !naturalImageDimensions) return;
    const containerRect = containerRef.current.getBoundingClientRect();
    const zoomCenterX = containerRect.width / 2;
    const zoomCenterY = containerRect.height / 2;

    const prevZoom = zoomLevel;
    const nextZoom = Math.max(0.25, Math.min(prevZoom + zoomIncrement, 5)); // Clamp

    if (prevZoom === nextZoom || !naturalImageDimensions) return;

    const newPan = adjustPanForZoom(prevZoom, nextZoom, zoomCenterX, zoomCenterY);
    
    setZoomLevel(nextZoom);
    setPan(newPan);
  };

  const handleMouseDown = (e) => {
    if (e.button !== 0 || !naturalImageDimensions) return;
    setIsPanning(true);
    setPanStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
    if (containerRef.current) containerRef.current.style.cursor = 'grabbing';
  };

  const handleMouseMove = (e) => {
    if (!isPanning || !naturalImageDimensions) return;
    setPan({ x: e.clientX - panStart.x, y: e.clientY - panStart.y });
  };

  const handleMouseUpOrLeave = () => {
    if (isPanning) {
      setIsPanning(false);
      if (containerRef.current) containerRef.current.style.cursor = 'grab';
    }
  };

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !naturalImageDimensions) return;

    const handleWheel = (e) => {
      e.preventDefault();
      const rect = container.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;
      const prevZoom = zoomLevel;
      let nextZoom;
      if (e.deltaY < 0) { // Zoom in
        nextZoom = Math.min(prevZoom * 1.05, 5);
      } else { // Zoom out
        nextZoom = Math.max(prevZoom / 1.05, 0.25);
      }
      if (prevZoom === nextZoom) return;
      const newPan = adjustPanForZoom(prevZoom, nextZoom, mouseX, mouseY);
      setZoomLevel(nextZoom);
      setPan(newPan);
    };

    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => container.removeEventListener('wheel', handleWheel, { passive: false });
  }, [zoomLevel, pan, naturalImageDimensions, adjustPanForZoom]); // panStart removed as pan and zoomLevel cover its context for adjustPanForZoom

  // Helper function to calculate distance between two touch points
  const getDistance = (p1, p2) => {
    return Math.sqrt(Math.pow(p2.clientX - p1.clientX, 2) + Math.pow(p2.clientY - p1.clientY, 2));
  };

  // Helper function to calculate midpoint between two touch points relative to the container
  const getMidpoint = (p1, p2, containerRect) => {
    return {
      x: (p1.clientX + p2.clientX) / 2 - containerRect.left,
      y: (p1.clientY + p2.clientY) / 2 - containerRect.top,
    };
  };

  const handleTouchStart = useCallback((e) => {
    if (!naturalImageDimensions || !containerRef.current) return;

    if (e.touches.length === 1) {
      // For single touch, prevent default to stop page scroll, etc.
      e.preventDefault();
      setIsPanning(true);
      setIsPinching(false);
      setPanStart({
        x: e.touches[0].clientX - pan.x,
        y: e.touches[0].clientY - pan.y,
      });
    } else if (e.touches.length === 2) {
      // For two touches, also prevent default.
      e.preventDefault(); 
      setIsPanning(false);
      setIsPinching(true);
      const t1 = e.touches[0];
      const t2 = e.touches[1];
      pinchStateRef.current = {
        initialDistance: getDistance(t1, t2),
        initialZoom: zoomLevel,
      };
    }
  }, [naturalImageDimensions, pan, zoomLevel, pinchStateRef, containerRef]);

  const handleTouchMove = useCallback((e) => {
    if (!naturalImageDimensions || !containerRef.current) return;

    if (isPanning && e.touches.length === 1) {
      // Prevent default for panning to avoid page scroll.
      e.preventDefault(); 
      setPan({
        x: e.touches[0].clientX - panStart.x,
        y: e.touches[0].clientY - panStart.y,
      });
    } else if (isPinching && e.touches.length === 2) {
      e.preventDefault();
      const t1 = e.touches[0];
      const t2 = e.touches[1];
      const currentDistance = getDistance(t1, t2);

      if (pinchStateRef.current.initialDistance === 0) return;

      const scaleFactor = currentDistance / pinchStateRef.current.initialDistance;
      const nextZoom = Math.max(0.25, Math.min(pinchStateRef.current.initialZoom * scaleFactor, 5));

      const prevZoom = zoomLevel;
      if (prevZoom === nextZoom) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const pinchMidpoint = getMidpoint(t1, t2, containerRect);
      const newPan = adjustPanForZoom(prevZoom, nextZoom, pinchMidpoint.x, pinchMidpoint.y);
      
      setZoomLevel(nextZoom);
      setPan(newPan);
    }
  }, [naturalImageDimensions, isPanning, isPinching, panStart, zoomLevel, pan, pinchStateRef, containerRef, adjustPanForZoom]);

  const handleTouchEndOrCancel = useCallback((e) => {
    // It can be useful to prevent default on touchend/cancel in some scenarios
    // to avoid unintended behaviors like focus changes or "ghost clicks".
    // Though, be cautious as it might interfere with other default behaviors if not handled carefully.
    // e.preventDefault(); // Consider if this is truly needed for touchend/cancel.

    if (e.type === 'touchcancel' || e.touches.length === 0) {
      if (isPanning) setIsPanning(false);
      if (isPinching) setIsPinching(false);
    } else if (e.touches.length === 1 && isPinching) { // Transition from pinch to pan
      setIsPinching(false);
      setIsPanning(true);
      // Update panStart for the remaining finger
      setPanStart({
        x: e.touches[0].clientX - pan.x,
        y: e.touches[0].clientY - pan.y,
      });
    }
    // Reset cursor for mouse, not strictly needed for touch but harmless
    // Only reset cursor if it's not a touch-only device or if mouse interaction is also expected
    if (containerRef.current && (e.type === 'touchcancel' || e.touches.length === 0) && !('ontouchstart' in window)) {
        containerRef.current.style.cursor = (naturalImageDimensions ? 'grab' : 'default');
    }
  }, [isPanning, isPinching, pan, naturalImageDimensions, containerRef]);

  // Effect for attaching touch event listeners directly with passive: false
  useEffect(() => {
    const currentContainer = containerRef.current;
    if (!currentContainer || !naturalImageDimensions) return;

    const options = { passive: false };

    currentContainer.addEventListener('touchstart', handleTouchStart, options);
    currentContainer.addEventListener('touchmove', handleTouchMove, options);
    currentContainer.addEventListener('touchend', handleTouchEndOrCancel, options);
    currentContainer.addEventListener('touchcancel', handleTouchEndOrCancel, options);

    return () => {
      currentContainer.removeEventListener('touchstart', handleTouchStart, options);
      currentContainer.removeEventListener('touchmove', handleTouchMove, options);
      currentContainer.removeEventListener('touchend', handleTouchEndOrCancel, options);
      currentContainer.removeEventListener('touchcancel', handleTouchEndOrCancel, options);
    };
  }, [naturalImageDimensions, handleTouchStart, handleTouchMove, handleTouchEndOrCancel]);

  const showMarker = naturalImageDimensions && selectedItem && typeof selectedItem.X === 'number' && typeof selectedItem.Y === 'number';
  const showNoCoordsMessage = naturalImageDimensions && selectedItem && (typeof selectedItem.X !== 'number' || typeof selectedItem.Y !== 'number');

  return (
    <div>

    <KernteamLayout>
      <div className="container">
        <h1 className="section-title blue">Stadtplan</h1>
        <h3>Ort</h3>
        <div className={`dropdown-wrapper ${selectedItem ? 'filled' : ''}`}>
          <select onChange={handleItemChange} value={selectedItem ? (selectedItem.JobID ? `job-${selectedItem.JobID}` : `offer-${selectedItem.ID}`) : ''} disabled={!jobs.length && !offers.length}>
            <option value="" disabled>Wähle einen Job oder ein Freizeitangebot</option>
            <optgroup label="Jobs">
              {jobs.map(job => (
                <option key={`job-${job.JobID}`} value={`job-${job.JobID}`}>{job.Name}</option>
              ))}
            </optgroup>
            <optgroup label="Freizeitangebote">
              {offers.map(offer => (
                <option key={`offer-${offer.ID}`} value={`offer-${offer.ID}`}>{offer.Name}</option>
              ))}
            </optgroup>
          </select>
          <span className="dropdown-plus material-symbols-outlined">keyboard_arrow_down</span>
        </div>
      </div>

      <div 
        ref={containerRef}
        className="stadtplan-container"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUpOrLeave}
        onMouseLeave={handleMouseUpOrLeave}
        // onTouchStart, onTouchMove, onTouchEnd, onTouchCancel are now handled by useEffect
        // This is to ensure { passive: false } is correctly applied.
        // React's synthetic event system might not always allow this level of control
        // for touch events as easily as direct DOM listeners.
      >
        <img 
          ref={stadtplanRef} 
          src={Stadtplan_Bild} 
          alt="Stadtplan" 
          className="stadtplan-image"
          style={{ 
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoomLevel})`,
            transformOrigin: '0 0',
            cursor: isPanning ? 'grabbing' : (naturalImageDimensions ? 'grab' : 'default'),
          }}
          draggable="false"
        />
        {showMarker && (
          <img
            src={Stadtplan_Marker}
            className="stadtplan-marker"
            style={getMarkerPosition()}
            alt={selectedItem.Name}
          />
        )}
        {!naturalImageDimensions && (
          <div className="stadtplan-message"><p>Karte wird geladen...</p></div>
        )}
        {showNoCoordsMessage && (
          <div className="stadtplan-message"><p>Für dieses Item sind keine Koordinaten auf dem Stadtplan hinterlegt.</p></div>
        )}
      </div>
      </KernteamLayout>
    </div>
  );
}

export default Stadtplan;
